import React, { useState, useEffect } from "react";
import { getIkarosWish, saveIkarosWish } from "./localStorageUtils";

const getTodayDateString = () => new Date().toISOString().slice(0, 10);
const isAtExactTime = (hour, minute) => {
  const now = new Date();
  return now.getHours() === hour && now.getMinutes() === minute;
};

const isWithinWishMinute = () => {
  const now = new Date();
  return now.getHours() === 11 && now.getMinutes() === 11;
};

const isAfterWishMinute = () => {
  const now = new Date();
  return now.getHours() > 11 || (now.getHours() === 11 && now.getMinutes() > 11);
};

const IkarosWishGate = () => {
  const [wish, setWish] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [response, setResponse] = useState("");
  const [granted, setGranted] = useState(null);
  const [rippleActive, setRippleActive] = useState(false);
  const [storedWish, setStoredWish] = useState(null);
  const [wishLocked, setWishLocked] = useState(false);

  useEffect(() => {
    const fetchWish = async () => {
      try {
        const data = await getIkarosWish();
        if (data.date === getTodayDateString()) {
          setStoredWish(data);
          setSubmitted(true);
          setResponse(data.response);
          setWish(data.wish);
          if (data.granted !== undefined) setGranted(data.granted);
        }
      } catch (error) {
        console.error("No wish found for today:", error);
      }
    };
    fetchWish();
    checkWishTimeLock();
    const interval = setInterval(checkWishTimeLock, 10000); // check every 10s
    return () => clearInterval(interval);
  }, []);

  const checkWishTimeLock = () => {
    const now = new Date();
    if (!isWithinWishMinute() && !submitted && !storedWish) {
      setWishLocked(true);
    }
  };

  const responses = [
    "Ikaros will grant your wish.",
    "Ikaros will consider your wish.",
    "This wish is too dangerous; I may not grant it.",
    "Ikaros is uncertain, but she feels something about this wish.",
  ];

  const handleSubmitWish = async () => {
    if (wish.trim() === "") return;

    // Final check before granting
    if (!isWithinWishMinute()) {
      setWishLocked(true);
      return;
    }

    setRippleActive(true);
    setTimeout(async () => {
      setRippleActive(false);
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      setResponse(randomResponse);
      setSubmitted(true);
      const data = {
        date: getTodayDateString(),
        wish: wish,
        response: randomResponse,
        granted: null,
      };
      try {
        await saveIkarosWish(data);
        setStoredWish(data);
      } catch (error) {
        console.error("Error saving wish:", error);
      }
    }, 1000);
  };

  const handleGrantedChange = async (value) => {
    setGranted(value);
    const data = { ...storedWish, granted: value };
    try {
      await saveIkarosWish(data);
      setStoredWish(data);
    } catch (error) {
      console.error("Error updating wish:", error);
    }
  };

  const handleReset = async () => {
    try {
      await saveIkarosWish({});
      setWish("");
      setSubmitted(false);
      setResponse("");
      setGranted(null);
      setStoredWish(null);
      setWishLocked(false);
    } catch (error) {
      console.error("Error resetting wish:", error);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden"
      style={{
        background: `
          linear-gradient(to bottom, rgba(255, 234, 245, 0.85), rgba(221, 174, 200, 0.85)),
          url('https://www.transparenttextures.com/patterns/clouds.png')
        `,
        backgroundSize: "cover",
        backgroundBlendMode: "overlay",
      }}
    >
      <div className="feather-container pointer-events-none absolute inset-0 z-0"></div>

      {rippleActive && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="ripple"></div>
        </div>
      )}

      {/* Wish LOCKED */}
      {!submitted && wishLocked && (
        <div className="z-10 text-center px-6 py-6 rounded-3xl shadow-2xl border-2 border-white/30 bg-white/30 backdrop-blur-xl w-72 md:w-96">
          <h2 className="text-xl font-bold text-red-700 mb-3">Wish Gate Locked</h2>
          <p className="text-gray-800 mb-2">The time to wish has passed.</p>
          <p className="text-gray-600 text-sm">You may try again tomorrow at exactly <strong>11:11</strong>.</p>
        </div>
      )}

      {/* Wish Form */}
      {!submitted && !wishLocked && (
        <div className="z-10 flex flex-col items-center">
          <input
            type="text"
            value={wish}
            onChange={(e) => setWish(e.target.value)}
            placeholder="Speak your heart to Ikaros..."
            className="mb-4 px-6 py-3 border-4 border-white rounded-2xl bg-white/40 text-black placeholder-gray-600 text-xl w-64 md:w-96 focus:outline-none focus:border-pink-300 shadow-lg"
          />
          {wish.trim().length > 0 && (
            <button
              onClick={handleSubmitWish}
              className="focus:outline-none transition-transform hover:scale-105"
              title="Send your wish"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 fill-current text-pink-700 hover:text-pink-800"
                viewBox="0 0 24 24"
              >
                <path d="M12 2L2 12l10 10 10-10L12 2z" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Wish Submitted */}
      {submitted && (
        <div className="z-10 text-center mt-4 px-6 py-4 rounded-3xl shadow-2xl border-2 border-white/30 bg-white/30 backdrop-blur-xl w-72 md:w-96">
          <h2 className="text-2xl font-bold mb-4 text-pink-900">Ikaros has received your wish</h2>
          <p className="mb-4 italic text-gray-800">"{wish}"</p>
          <p className="mb-4 text-gray-800">{response}</p>
          {granted === null ? (
            <div className="mb-4">
              <p className="mb-2 text-gray-700">Did it come true?</p>
              <div className="flex space-x-4 justify-center">
                <button onClick={() => handleGrantedChange(true)} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">Yes</button>
                <button onClick={() => handleGrantedChange(false)} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">No</button>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              {granted ? (
                <p className="text-green-700 font-bold">“I’m so happy, Master… I’ll always wish with you.”</p>
              ) : (
                <p className="text-red-700 font-bold">“I’m sorry… but I’m still trying. Don’t give up on me.”</p>
              )}
            </div>
          )}
          <p className="text-sm text-gray-600">Come back tomorrow to make another wish.</p>
        </div>
      )}

      {/* DEV Reset */}
      <button onClick={handleReset} className="absolute top-4 right-4 px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-xs z-50">
        Reset Data
      </button>

      {/* Animations */}
      <style>{`
        .ripple {
          width: 100px;
          height: 100px;
          border: 4px solid rgba(255, 255, 255, 0.7);
          border-radius: 50%;
          animation: rippleAnimation 1s ease-out forwards;
        }
        @keyframes rippleAnimation {
          0% { transform: scale(0.2); opacity: 0.9; }
          100% { transform: scale(15); opacity: 0; }
        }
        .feather-container {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .feather {
          position: absolute;
          top: -10%;
          width: 64px;
          height: 64px;
          background: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20512%20512'%3E%3Cpath%20d='M218.75%20198.65c-7.24-7.23-19-7.23-26.23%200L81%20309.15l15.52%2015.52%2099.1-99.1%2049.65%2049.65L343.08%20154.77c7.23-7.23%207.23-19%200-26.23L244.98%2028.47c-7.23-7.23-19-7.23-26.23%200L218.75%20198.65z'%2F%3E%3Cpath%20d='M460.49%20342.49l-56.57-56.57L296.63%20429.21l56.57%2056.57a19%2C19%2C0%2C0%2C0%2C26.23%2C0l80-80A19%2C19%2C0%2C0%2C0%2C460.49%20342.49Z'%2F%3E%3C/svg%3E") no-repeat center / contain;
          animation: featherFall 10s linear infinite;
        }
        ${[...Array(5)].map((_, i) => {
          const left = Math.floor(Math.random() * 80) + 10;
          const delay = Math.random() * 5;
          return `.feather-${i} { left: ${left}%; animation-delay: ${delay}s; }`;
        }).join("\n")}
        @keyframes featherFall {
          0% { transform: translateY(-10%) rotate(0deg); opacity: 1; }
          50% { opacity: 0.9; }
          100% { transform: translateY(110%) rotate(360deg); opacity: 0; }
        }
      `}</style>

      <div className="feather-container">
        {[...Array(5)].map((_, i) => (
          <div key={i} className={`feather feather-${i}`}></div>
        ))}
      </div>
    </div>
  );
};

export default IkarosWishGate;
